import React, { useState, useEffect } from 'react';
import { Button, message, Space, Spin } from 'antd';
import axios from 'axios';
import GenericTable from '../components/GenericTable'; // Import the generic table
import GenericModal from '../components/GenericModal '; // Import the generic modal
import './Models.css';
import AppMenu from '../components/AppMenu';
import { format } from 'date-fns';
import GenericViewDrawer from "../components/GenericViewModal";

const Avatars = () => {
    const [avatars, setAvatars] = useState([]);
    const [loading, setLoading] = useState(true); // For overall page loading state
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentAvatar, setCurrentAvatar] = useState(null); // For editing a specific avatar
    const [initialFormData, setInitialFormData] = useState({}); // Track initial form data

    const [isViewDrawerVisible, setIsViewDrawerVisible] = useState(false);
    const [viewDrawerData, setViewDrawerData] = useState(null);

    const [page, setPage] = useState(1); // Current page
    const [limit] = useState(10); // Items per page
    const [search, setSearch] = useState(""); // Search query
    const [total, setTotal] = useState(0);

    useEffect(() => {
        fetchAvatars();
    }, []);

    useEffect(() => {
        fetchAvatars();
    }, [search]);
    const fetchAvatars = async (currentPage) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API}/api/avatar`, {
                params: { page: currentPage || page, limit, search },
            });
            setAvatars(response.data.data);
            setTotal(response.data.meta.total);
        } catch (error) {
            console.error('Error fetching avatars:', error);
            message.error('Failed to load avatars');
        } finally {
            setLoading(false);
        }
    };

    // Format avatars data to display in the table
    const formatAvatarsData = () => {
        return avatars.map((avatar) => ({
            id: avatar.id,
            name: avatar.name,
            status: avatar.status,
            createdAt: format(new Date(avatar.createdAt), 'MM/dd/yyyy, HH:mm:ss'),
            updatedAt: format(new Date(avatar.updatedAt), 'MM/dd/yyyy, HH:mm:ss'),
        }));
    };

    const handleSearchChange = (value) => {
        setSearch(value);
    };

    // Create or update avatar
    const handleCreateUpdateAvatar = async (formData) => {
        try {
            if (currentAvatar) {
                // Update avatar
                await axios.patch(`${process.env.REACT_APP_API}/api/avatar/${currentAvatar.id}`, formData);
                message.success('Avatar updated successfully');
            } else {
                // Create new avatar
                await axios.post(`${process.env.REACT_APP_API}/api/avatar`, formData);
                message.success('Avatar created successfully');
            }

            fetchAvatars(); // Refresh the avatars list
            closeModal(); // Close modal after action
            setCurrentAvatar(null);
        } catch (error) {
            console.error('Error creating/updating avatar:', error);
            message.error('Failed to save avatar');
        }
    };

    // Toggle avatar status
    const handleToggleAvatarStatus = async (avatar) => {
        try {
            await axios.patch(`${process.env.REACT_APP_API}/api/avatar/${avatar.id}/status`, {});
            message.success('Avatar status updated successfully');
            fetchAvatars(); // Refresh the avatars list after update
        } catch (error) {
            console.error('Error updating avatar status:', error);
            message.error('Failed to update avatar status');
        }
    };

    const handleViewDetails = (avatar) => {
        setViewDrawerData(avatar);
        setIsViewDrawerVisible(true);
    };

    // Open modal to create or update avatar
    const openModal = (avatar = null) => {
        if (avatar) {
            setCurrentAvatar(avatar);
            setInitialFormData(avatar);
        } else {
            setInitialFormData({});
        }
        setIsModalVisible(true);
    };

    // Close modal
    const closeModal = () => {
        setIsModalVisible(false);
        setCurrentAvatar(null);
    };

    // Actions for the GenericTable
    const actions = ['Toggle Status', 'View', 'Edit'];
    const actionFunctions = [
        (avatar) => handleToggleAvatarStatus(avatar),
        (avatar) => handleViewDetails(avatar),
        (avatar) => openModal(avatar),
    ];

    if (loading) {
        return <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />;
    }

    return (
        <div style={{ display: "flex" }}>
            <AppMenu />
            <div className="avatars-page" style={{width:"100%"}}>
                <Space style={{ width: "98%", display: "flex", justifyContent: "end" }}>
                    <Button type="primary" onClick={() => openModal()}>
                        Create Avatar
                    </Button>
                </Space>

                {/* Generic Table to show avatars */}
                <GenericTable
                    data={formatAvatarsData()}
                    searchKeys={['name', 'status']}
                    sortableColumns={['name', 'status']}
                    actions={actions}
                    actionFunctions={actionFunctions}
                    onRowClick={openModal}
                    toggle="status"
                    pagination={{
                        current: page,
                        pageSize: limit,
                        total,
                        onChange: (pageNumber) => {
                            setPage(pageNumber);
                            fetchAvatars(pageNumber);
                        },
                    }}
                    onSearch={handleSearchChange}
                />

                {/* View Drawer */}
                <GenericViewDrawer
                    isVisible={isViewDrawerVisible}
                    handleCancel={() => setIsViewDrawerVisible(false)}
                    data={viewDrawerData}
                    title="Avatar Details"
                    footerButtons={["Close"]}
                    onButtonClick={() => setIsViewDrawerVisible(false)}
                />

                {/* Generic Modal for Create/Edit Avatar */}
                <GenericModal
                    isVisible={isModalVisible}
                    handleOk={(formData) => handleCreateUpdateAvatar(formData)}
                    handleCancel={closeModal}
                    title={currentAvatar ? 'Edit Avatar' : 'Create Avatar'}
                    inputs={[
                        { label: 'Name', name: 'name', type: 'text', placeholder: 'Enter avatar name', require: true },
                    ]}
                    actions={['Submit', 'Cancel']}
                    actionFunctions={[
                        (formData) => handleCreateUpdateAvatar(formData),
                        closeModal,
                    ]}
                    predefinedData={initialFormData}
                />
            </div>
        </div>
    );
};

export default Avatars;
